import { useEffect, useState } from 'react';
import { apiUrl } from '@/lib/shell';
import type { EntryCopy } from '@/i18n/ns/entry';
import { EntryButton, EntryHeading } from './entry-shell';

export interface CompanyInvitationPreview {
  company: { name: string };
  recipient: string;
}

export function useCompanyInvitation({ open, token }: { open: boolean; token: string | null }) {
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState<{
    token: string | null;
    data: CompanyInvitationPreview | null;
    error: 'invalid' | 'unavailable' | null;
    loading: boolean;
  }>({ token: null, data: null, error: null, loading: false });
  const enabled = open && Boolean(token);
  useEffect(() => {
    if (!enabled || !token) return;
    let active = true;
    setState({ token, data: null, error: null, loading: true });
    void fetch(apiUrl('/api/v1/company-invitation/preview'), {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }),
      cache: 'no-store',
    })
      .then(async (response) => {
        if (!active) return;
        if (!response.ok) {
          setState({
            token,
            data: null,
            loading: false,
            error: response.status === 403 ? 'invalid' : 'unavailable',
          });
          return;
        }
        const data = await response.json();
        if (
          typeof data?.company?.name !== 'string' ||
          typeof data?.recipient !== 'string' ||
          !data.recipient
        )
          throw new Error('invalid_preview');
        if (active) setState({ token, data, error: null, loading: false });
      })
      .catch(() => {
        if (active) setState({ token, data: null, loading: false, error: 'unavailable' });
      });
    return () => {
      active = false;
    };
  }, [enabled, token, attempt]);
  const current = enabled && state.token === token;
  return {
    data: current ? state.data : null,
    error: current ? state.error : null,
    loading: enabled && (!current || state.loading),
    retry: () => setAttempt((value) => value + 1),
  };
}

/**
 * An invitation that cannot open a sign-up form yet: still being checked, not
 * reachable, or no longer usable. One title, neutral body text and a next step.
 */
export function InvitationState({
  copy,
  loginLabel,
  state,
  onRetry,
  onLogin,
  onCreateCompany,
}: {
  copy: EntryCopy;
  loginLabel: string;
  state: 'loading' | 'unavailable' | 'invalid';
  onRetry: () => void;
  onLogin: () => void;
  onCreateCompany: () => void;
}) {
  if (state === 'loading') {
    return (
      <div role="status">
        <EntryHeading title={copy.checkingInvite} />
      </div>
    );
  }
  if (state === 'unavailable') {
    return (
      <>
        <EntryHeading title={copy.inviteUnavailableTitle} lead={copy.inviteUnavailableLead} />
        <div className="pc-entry-actions">
          <EntryButton block onClick={onRetry}>
            {copy.tryAgain}
          </EntryButton>
        </div>
      </>
    );
  }
  return (
    <>
      <EntryHeading title={copy.inviteInvalidTitle} lead={copy.inviteInvalidLead} />
      <div className="pc-entry-actions">
        <EntryButton block onClick={onLogin}>
          {loginLabel}
        </EntryButton>
        <EntryButton variant="secondary" block onClick={onCreateCompany}>
          {copy.createCompany}
        </EntryButton>
      </div>
    </>
  );
}
